import {ChangeDetectionStrategy, Component, OnInit, Self, Optional, ChangeDetectorRef, Input, ViewChild} from '@angular/core';
import {ControlValueAccessor, FormArray, NgControl, ValidatorFn, FormControl, FormBuilder} from '@angular/forms';
import {MatChipInput, MatChipInputEvent} from '@angular/material/chips';
import {WithCheckControlUpdates} from '../../constants/form-field.constant';
import {MessageService} from '../../services/message-service.service';
import {WithDestroy} from '../with-destroy/with-destroy.mixin';
import {AbstractControl} from '@angular/forms';

@Component({
  selector: 'app-chips',
  templateUrl: './chips.component.html',
  styleUrls: ['./chips.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'appChips',
  providers: []
})
export class ChipsComponent extends WithDestroy(WithCheckControlUpdates(self => self.chipsGroup)) implements OnInit, ControlValueAccessor {
  @Input() label: string;
  @Input() isRequired = false;
  @Input() testId = 'chips';
  @Input() fieldClass;
  @Input() valueValidator: () => ValidatorFn;
  @Input() type: 'number' | 'text' = 'text';
  @ViewChild('newChipInput', {
    read: MatChipInput
  })
  newChipInput: MatChipInput;

  chipsGroup = this.fb.group({
    elements: this.fb.array([])
  });

  constructor(
    @Self() @Optional() public ctrl: NgControl,
    private cd: ChangeDetectorRef,
    public messageService: MessageService,
    private fb: FormBuilder
  ) {
    super(cd);
    if (this.ctrl) {
      this.ctrl.valueAccessor = this;
    }
  }
  get formControl(): FormArray {
    return this.chipsGroup?.get('elements') as FormArray;
  }
  get values() {
    return this.formControl.value;
  }
  onChangeFn: (...args: any[]) => void;
  onTouchFn: (...args: any[]) => void;
  isDisabled = false;

  writeValue(value: any[]): void {
    if (value && !Array.isArray(value)) {
      throw new Error('Value should be array');
    }
    this.formControl.clear();
    if (value) {
      value.forEach(element => {
        this.formControl.push(new FormControl(element, this.valueValidator?.()));
      });
    }
  }
  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouchFn = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  ngOnInit(): void {}

  private update() {
    this.onChangeFn(this.formControl.value);
  }

  get value() {
    return this.formControl.controls.map(c => (this.type === 'number' ? +c.value : c.value));
  }

  get validValue() {
    return this.formControl.controls.filter(c => c.valid).map(c => (this.type === 'number' ? +c.value : c.value));
  }

  removeElement(formControl: AbstractControl) {
    this.formControl.removeAt(this.formControl.controls.findIndex(c => c === formControl));
    this.update();
  }

  onBackspace(keyboardEvent: KeyboardEvent, inputElement: any) {
    if (inputElement.value === '') {
      this.removeElement([...this.formControl.controls].pop());
    }
  }
  onPaste(event: ClipboardEvent) {
    let pastedText;
    if (event.clipboardData && event.clipboardData.getData) {
      pastedText = event.clipboardData.getData('text/plain');
      event.preventDefault();
      this.addElement({
        input: event.target as any,
        value: pastedText,
        chipInput: this.newChipInput
      });
    }
  }

  addElement(event: MatChipInputEvent) {
    if (event.value) {
      const multipleValues = event.value
        .split(/[,; \t\n]/g)
        .map(v => v.trim())
        .filter(v => !!v);
      multipleValues.forEach(value => {
        this.formControl.push(new FormControl(value, this.valueValidator ? [this.valueValidator?.()] : []));
      });
      event.chipInput.inputElement.value = '';
      this.update();
    }
    return true;
  }
}
